import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, FileText, Layers } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

// Strip common markdown symbols so responses look clean & solid
function cleanMd(text) {
    if (!text) return "";
    return text
        .replace(/^#{1,6}\s+/gm, "")          // headers
        .replace(/\*\*(.*?)\*\*/g, "$1")      // bold
        .replace(/__(.*?)__/g, "$1")           // bold alt
        .replace(/(^|\s)\*(?!\s)(.+?)(?<!\s)\*(?=\s|$|[.,!?])/g, "$1$2") // italic *x*
        .replace(/(^|\s)_(?!\s)(.+?)(?<!\s)_(?=\s|$|[.,!?])/g, "$1$2")   // italic _x_
        .replace(/`{1,3}([^`]*)`{1,3}/g, "$1") // inline / block code
        .replace(/^\s*[-*+]\s+/gm, "• ")       // bullets -> •
        .replace(/^\s*>\s?/gm, "")             // blockquote
        .replace(/^-{3,}$/gm, "")              // hr
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export function AIChat({ open, setOpen }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("chat");
    const sessionId = useRef(localStorage.getItem("ss_ai_session") || (() => {
        const s = "sess-" + Math.random().toString(36).slice(2, 10);
        localStorage.setItem("ss_ai_session", s);
        return s;
    })());
    const scroller = useRef(null);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get(`/ai/sessions/${sessionId.current}`);
                setMessages(data);
            } catch (e) { console.warn("[ai] failed to load history", e); }
        })();
    }, [open]);

    useEffect(() => {
        scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
    }, [messages, loading]);

    const send = async () => {
        const text = input.trim();
        if (!text || loading) return;
        setInput("");
        const now = new Date().toISOString();
        setMessages(m => [...m, { role: "user", content: text, created_at: now }, { role: "assistant", content: "", created_at: now, streaming: true }]);
        setLoading(true);
        try {
            const res = await fetch(`${API}/ai/chat`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message: text, mode }),
            });
            if (!res.ok) throw new Error("AI request failed");
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let acc = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                acc += dec.decode(value);
                setMessages(m => {
                    const copy = [...m];
                    copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc };
                    return copy;
                });
            }
            setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
                return copy;
            });
        } catch (e) {
            toast.error("AI error", { description: e.message });
            setMessages(m => m.slice(0, -1));
        } finally { setLoading(false); }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0" data-testid="ai-chat-panel">
                <SheetHeader className="p-6 pb-3 border-b border-border/60">
                    <SheetTitle className="flex items-center gap-2 font-display">
                        <Sparkles className="w-4 h-4 text-accent" /> StudySphere AI
                    </SheetTitle>
                    <div className="flex gap-2 mt-2">
                        {[["chat", "Chat", Sparkles], ["summarize", "Summarize", FileText], ["flashcards", "Flashcards", Layers]].map(([k, l, I]) => (
                            <button key={k} onClick={() => setMode(k)} data-testid={`ai-mode-${k}`}
                                className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 transition ${mode === k ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:bg-muted"}`}>
                                <I className="w-3 h-3" /> {l}
                            </button>
                        ))}
                    </div>
                </SheetHeader>

                <div ref={scroller} className="flex-1 overflow-y-auto p-6 space-y-4">
                    {messages.length === 0 && (
                        <div className="text-center py-10 text-sm text-muted-foreground">
                            <Sparkles className="w-6 h-6 mx-auto mb-3 text-accent" />
                            <div className="font-display text-base text-foreground mb-1">How can I help you focus today?</div>
                            <p className="text-xs">Ask questions, paste notes to summarize, or generate flashcards.</p>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={m.id || m.created_at || `msg-${i}`} className={`text-sm ${m.role === "user" ? "text-right" : "text-left"}`}>
                            <div className={`inline-block max-w-[85%] px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md"}`}>
                                {m.role === "user" ? m.content : cleanMd(m.content)}{m.streaming && <span className="inline-block w-1.5 h-3.5 bg-current/60 ml-1 animate-pulse-soft align-middle" />}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-border/60">
                    <Textarea
                        data-testid="ai-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={mode === "summarize" ? "Paste notes to summarize…" : mode === "flashcards" ? "Topic or notes for flashcards…" : "Ask anything…"}
                        className="min-h-[60px] resize-none"
                    />
                    <div className="flex justify-end mt-2">
                        <Button size="sm" onClick={send} disabled={loading} data-testid="ai-send">
                            <Send className="w-3.5 h-3.5 mr-1.5" /> {loading ? "Thinking…" : "Send"}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
